import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { usePropertySetupOptions } from '../../../components/form/property-select.js';
import { useCreateUser, useUpdateUser } from '../api/mutations.js';
import { useRoleOptions, useUser } from '../api/queries.js';
import {
  isSystemRole,
  userFormInput,
  type PropertyOption,
  type RoleOption,
  type UserDetail,
  type UserFormInput,
} from '../api/schemas.js';
import { UserFields } from '../forms/user-fields.js';

/**
 * Creating and editing a tenant user.
 *
 * The form itself is separated from the loading shell so hooks are never
 * called conditionally. The shell loads the user and the pickers, then renders
 * the form with the data it needs.
 */

function roleIdsFor(detail: UserDetail, roleOptions: readonly RoleOption[]): string[] {
  const byName = new Map(
    roleOptions.filter((role) => !isSystemRole(role)).map((role) => [role.name, role.id]),
  );
  for (const name of detail.roles) {
    const id = byName.get(name);
    if (id !== undefined) {
      return [id];
    }
  }
  return [];
}

interface FormFieldsProps {
  readonly roleOptions: readonly RoleOption[];
  readonly propertyOptions: readonly PropertyOption[];
}

export function UserFormPage(): React.JSX.Element {
  const { userId } = useParams();

  return (
    <PermissionGuard permission={PERMISSIONS.USER_WRITE} fallback={<NotPermitted />}>
      {userId !== undefined ? <EditUserShell userId={userId} /> : <CreateUserShell />}
    </PermissionGuard>
  );
}

function CreateUserShell(): React.JSX.Element {
  const roleQuery = useRoleOptions();
  const setupQuery = usePropertySetupOptions();

  if (roleQuery.isPending || setupQuery.isPending) {
    return <LoadingState label="Loading form options" />;
  }

  if (roleQuery.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(roleQuery.error) ?? 'The role list could not be loaded.'}
      />
    );
  }

  if (setupQuery.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(setupQuery.error) ?? 'The property list could not be loaded.'}
      />
    );
  }

  return <CreateUserForm roleOptions={roleQuery.data} propertyOptions={setupQuery.data} />;
}

function CreateUserForm({ roleOptions, propertyOptions }: FormFieldsProps): React.JSX.Element {
  const navigate = useNavigate();
  const create = useCreateUser();

  const form = useAppForm<UserFormInput>({
    schema: userFormInput,
    defaultValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      status: 'ACTIVE',
      roleIds: [],
      propertyIds: [],
    },
    successMessage: 'User created',
    onSubmit: async (values) => {
      const created = await create.mutateAsync(values);
      void navigate(`/users/${created.id}`);
    },
  });

  return (
    <FormPage
      title="New user"
      description="Create a staff account and assign the roles and properties it needs."
      backTo="/users"
      backLabel="Users"
      submitLabel="Create user"
      submitting={form.submitting}
      onSubmit={() => {
        void form.submit();
      }}
      dirty={form.formState.isDirty}
    >
      <UserFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        setValue={form.setValue}
        mode="create"
        roleOptions={roleOptions}
        propertyOptions={propertyOptions}
      />
    </FormPage>
  );
}

function EditUserShell({ userId }: { readonly userId: string }): React.JSX.Element {
  const userQuery = useUser(userId);
  const roleQuery = useRoleOptions();
  const setupQuery = usePropertySetupOptions();

  if (userQuery.isPending || roleQuery.isPending || setupQuery.isPending) {
    return <LoadingState label="Loading user" />;
  }

  if (userQuery.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(userQuery.error) ?? 'This user could not be loaded.'}
        onRetry={() => {
          void userQuery.refetch();
        }}
      />
    );
  }

  if (roleQuery.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(roleQuery.error) ?? 'The role list could not be loaded.'}
      />
    );
  }

  if (setupQuery.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(setupQuery.error) ?? 'The property list could not be loaded.'}
      />
    );
  }

  return (
    <UserEditForm
      detail={userQuery.data}
      roleOptions={roleQuery.data}
      propertyOptions={setupQuery.data}
    />
  );
}

interface UserEditFormProps extends FormFieldsProps {
  readonly detail: UserDetail;
}

function UserEditForm({
  detail,
  roleOptions,
  propertyOptions,
}: UserEditFormProps): React.JSX.Element {
  const navigate = useNavigate();
  const update = useUpdateUser();

  const form = useAppForm<UserFormInput>({
    schema: userFormInput,
    defaultValues: {
      email: detail.email,
      firstName: detail.firstName ?? '',
      lastName: detail.lastName ?? '',
      status: detail.status as UserFormInput['status'],
      roleIds: roleIdsFor(detail, roleOptions),
      propertyIds: [...detail.propertyIds],
    },
    successMessage: 'User updated',
    onSubmit: async (values) => {
      await update.mutateAsync({ userId: detail.id, values });
      void navigate(`/users/${detail.id}`);
    },
  });

  return (
    <FormPage
      title={detail.displayName}
      description="Update the account's name, status, roles and property access."
      backTo="/users"
      backLabel="Users"
      submitLabel="Save changes"
      submitting={form.submitting}
      onSubmit={() => {
        void form.submit();
      }}
      dirty={form.formState.isDirty}
    >
      <UserFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        setValue={form.setValue}
        mode="edit"
        roleOptions={roleOptions}
        propertyOptions={propertyOptions}
      />
    </FormPage>
  );
}
